import {
  AuthenticationResult,
  BrowserAuthError,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { AxiosResponse } from 'axios';
import { tokenRequest } from '../common/AuthConfig';
import { redirectToLogin } from '../common/AuthToken';
import { getAuthClient, isAuth0Provider } from '../common/auth/authClient';
import axiosClient from '../common/AxiosClient';
import { Utils } from '../common/Utils';
import { DataContext, DataOptions, DataRequestOptions, HttpMethod } from '../types/data.types';

export class Data<T = unknown> {
  static apiBasePath = `${window.app.env.API_URL}/api`;

  isRegaining: boolean;
  cached: boolean;
  api: string | undefined;
  method: HttpMethod;
  requestOpts: DataRequestOptions;
  dataReceived: boolean;

  constructor(options: DataOptions<T>) {
    const { api, reject, callback, context, method, postData } = options;

    this.isRegaining = false;
    this.cached = false;
    this.api = api;
    this.method = method || 'get';
    this.requestOpts = { data: postData };
    this.dataReceived = false;

    if (api) {
      if (context.devMode) {
        const mimicApiDown = Utils.getDevOption('devOpts_MimicApiDown');
        if (mimicApiDown) {
          reject({ message: 'Network Error' });
          return;
        }
      }

      this.getData(context)
        .then((data) => callback(data))
        .catch((e) => reject(e));
    }
  }

  getData(context: DataContext): Promise<T> {
    return new Promise((resolve, reject) => {
      Data.getAccessToken(context)
        .then((accessToken) => {
          if (!accessToken) return;

          this.onGetToken(accessToken);

          Data.call(this.method, this.api!, { ...this.requestOpts })
            .then((response) => {
              this.onDataReceived(response.data);
              this.dataReceived = true;
              resolve(response.data);
            })
            .catch((error) => {
              if (error.response && error.response.status === 302) {
                window.location.href = '/signin-oic';
              }
              reject(error);
            })
            .finally(() => {
              this.cached = true;
            });
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  static call(
    method: HttpMethod,
    apiPath: string,
    opts?: DataRequestOptions
  ): Promise<AxiosResponse> {
    return axiosClient({
      method,
      url: apiPath,
      withCredentials: true,
      maxRedirects: 0,
      ...opts,
    });
  }

  static get<T>(context: DataContext, api: string): Promise<AxiosResponse<T>> {
    return new Promise((resolve, reject) => {
      Data.getAccessToken(context)
        .then((accessToken) => {
          if (!accessToken) return;
          return Data.call('get', api)
            .then((response) => resolve(response))
            .catch((error) => reject(error));
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onDataReceived(_data: unknown): void {}

  updateData(context: DataContext): Promise<unknown> {
    return this.getData(context);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onGetToken(_accessToken: string): void {}

  // Returns the token for callers that need it in hand (the SignalR accessTokenFactory, the dev
  // dialog). It must NOT be written to axiosClient.defaults: a default header outlives the token it
  // was taken from, so every request that later skipped token injection replayed this one long
  // after it expired. AxiosClient's request interceptor is the only place that attaches it.
  static getAccessToken(context: DataContext): Promise<string | null> {
    if (isAuth0Provider()) {
      // Auth0 has no MSAL context; the B2C authority check below does not apply.
      const client = getAuthClient();
      return client.getAccessToken().catch((error: unknown) => {
        if (client.isInteractionRequired(error)) {
          redirectToLogin();
          return null;
        }
        throw error;
      });
    }

    if (context.msal?.accounts[0].environment !== window.app.env.B2C_KNOWN_AUTHORITY) {
      const logoutRequest = {
        account: context.msal?.accounts[0],
      };
      context.msal?.instance.logoutRedirect(logoutRequest);
      return Promise.reject(new Error('Invalid B2C authority'));
    }

    const accessTokenRequest = {
      ...tokenRequest,
      account: context.msal?.accounts[0],
      redirectUri: '/',
    };

    return new Promise((resolve, reject) => {
      context.msal?.instance
        .acquireTokenSilent(accessTokenRequest)
        .then((response: AuthenticationResult) => {
          resolve(response.accessToken);
        })
        .catch((error: Error) => {
          // fallback to interaction when silent call fails
          if (error instanceof InteractionRequiredAuthError || error instanceof BrowserAuthError) {
            redirectToLogin();
            resolve(null);
          } else {
            reject(error);
          }
        });
    });
  }

  static getFromData<T extends Record<string, unknown>>(
    data: T,
    key: string
  ): T[keyof T] | undefined {
    const foundKey = Object.keys(data).find((k) => k.toLowerCase() === key.toLowerCase());
    return foundKey ? data[foundKey as keyof T] : undefined;
  }
}
